/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row, UncontrolledTooltip } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getClinicalDictionaries } from 'app/entities/clinical-dictionary/clinical-dictionary.reducer';
import { getEntities as getWorkFlowProtocols } from 'app/entities/work-flow-protocol/work-flow-protocol.reducer';
import { createEntity, getEntity, reset, updateEntity } from './work-flow-step.reducer';

export const WorkFlowStepUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const clinicalDictionaries = useAppSelector(state => state.clinicalDictionary.entities);
  const workFlowProtocols = useAppSelector(state => state.workFlowProtocol.entities);
  const workFlowStepEntity = useAppSelector(state => state.workFlowStep.entity);
  const loading = useAppSelector(state => state.workFlowStep.loading);
  const updating = useAppSelector(state => state.workFlowStep.updating);
  const updateSuccess = useAppSelector(state => state.workFlowStep.updateSuccess);

  const handleClose = () => {
    navigate(`/work-flow-step${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getClinicalDictionaries({}));
    dispatch(getWorkFlowProtocols({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.sequence !== undefined && typeof values.sequence !== 'number') {
      values.sequence = Number(values.sequence);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastUpdated !== undefined && typeof values.lastUpdated !== 'number') {
      values.lastUpdated = Number(values.lastUpdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampEntry !== undefined && typeof values.timestampEntry !== 'number') {
      values.timestampEntry = Number(values.timestampEntry);
    }

    const entity = {
      ...workFlowStepEntity,
      ...values,
      role: clinicalDictionaries.find(it => it.id.toString() === values.role?.toString()),
      stepType: clinicalDictionaries.find(it => it.id.toString() === values.stepType?.toString()),
      protocol: workFlowProtocols.find(it => it.id.toString() === values.protocol?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          ...workFlowStepEntity,
          role: workFlowStepEntity?.role?.id,
          stepType: workFlowStepEntity?.stepType?.id,
          protocol: workFlowStepEntity?.protocol?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.workFlowStep.home.createOrEditLabel" data-cy="WorkFlowStepCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.workFlowStep.home.createOrEditLabel">Create or edit a WorkFlowStep</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="work-flow-step-id"
                  label={translate('tinyMedicRestApp.workFlowStep.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.workFlowStep.name')}
                id="work-flow-step-name"
                name="name"
                data-cy="name"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <UncontrolledTooltip target="nameLabel">
                <Translate contentKey="tinyMedicRestApp.workFlowStep.help.name" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.workFlowStep.description')}
                id="work-flow-step-description"
                name="description"
                data-cy="description"
                type="text"
              />
              <UncontrolledTooltip target="descriptionLabel">
                <Translate contentKey="tinyMedicRestApp.workFlowStep.help.description" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.workFlowStep.sequence')}
                id="work-flow-step-sequence"
                name="sequence"
                data-cy="sequence"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="sequenceLabel">
                <Translate contentKey="tinyMedicRestApp.workFlowStep.help.sequence" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.workFlowStep.approvalRequired')}
                id="work-flow-step-approvalRequired"
                name="approvalRequired"
                data-cy="approvalRequired"
                check
                type="checkbox"
              />
              <UncontrolledTooltip target="approvalRequiredLabel">
                <Translate contentKey="tinyMedicRestApp.workFlowStep.help.approvalRequired" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.workFlowStep.unitsOfMeasure')}
                id="work-flow-step-unitsOfMeasure"
                name="unitsOfMeasure"
                data-cy="unitsOfMeasure"
                type="text"
              />
              <UncontrolledTooltip target="unitsOfMeasureLabel">
                <Translate contentKey="tinyMedicRestApp.workFlowStep.help.unitsOfMeasure" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.workFlowStep.valueType')}
                id="work-flow-step-valueType"
                name="valueType"
                data-cy="valueType"
                type="text"
              />
              <UncontrolledTooltip target="valueTypeLabel">
                <Translate contentKey="tinyMedicRestApp.workFlowStep.help.valueType" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.workFlowStep.valueRange')}
                id="work-flow-step-valueRange"
                name="valueRange"
                data-cy="valueRange"
                type="text"
              />
              <UncontrolledTooltip target="valueRangeLabel">
                <Translate contentKey="tinyMedicRestApp.workFlowStep.help.valueRange" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.workFlowStep.active')}
                id="work-flow-step-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <UncontrolledTooltip target="activeLabel">
                <Translate contentKey="tinyMedicRestApp.workFlowStep.help.active" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.workFlowStep.cursor')}
                id="work-flow-step-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="cursorLabel">
                <Translate contentKey="tinyMedicRestApp.workFlowStep.help.cursor" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.workFlowStep.lastUpdated')}
                id="work-flow-step-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="lastUpdatedLabel">
                <Translate contentKey="tinyMedicRestApp.workFlowStep.help.lastUpdated" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.workFlowStep.microSec')}
                id="work-flow-step-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="microSecLabel">
                <Translate contentKey="tinyMedicRestApp.workFlowStep.help.microSec" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.workFlowStep.timestampEntry')}
                id="work-flow-step-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="timestampEntryLabel">
                <Translate contentKey="tinyMedicRestApp.workFlowStep.help.timestampEntry" />
              </UncontrolledTooltip>
              <ValidatedField
                id="work-flow-step-role"
                name="role"
                data-cy="role"
                label={translate('tinyMedicRestApp.workFlowStep.role')}
                type="select"
              >
                <option value="" key="0" />
                {clinicalDictionaries
                  ? clinicalDictionaries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="work-flow-step-stepType"
                name="stepType"
                data-cy="stepType"
                label={translate('tinyMedicRestApp.workFlowStep.stepType')}
                type="select"
              >
                <option value="" key="0" />
                {clinicalDictionaries
                  ? clinicalDictionaries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="work-flow-step-protocol"
                name="protocol"
                data-cy="protocol"
                label={translate('tinyMedicRestApp.workFlowStep.protocol')}
                type="select"
              >
                <option value="" key="0" />
                {workFlowProtocols
                  ? workFlowProtocols.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/work-flow-step" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default WorkFlowStepUpdate;
